import { Capacitor } from '@capacitor/core';

/**
 * Utility class to gather device and browser information
 */
export class DeviceInfo {
  /**
   * Get comprehensive device and browser information
   */
  public static getDeviceInfo() {
    return {
      // Browser information
      userAgent: navigator.userAgent,
      appVersion: navigator.appVersion,
      platform: navigator.platform,
      vendor: navigator.vendor,
      language: navigator.language,
      
      // Screen information
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
      screenOrientation: window.screen.orientation?.type || 'unknown',
      
      // Capacitor information (if available)
      isNativePlatform: Capacitor.isNativePlatform(),
      capacitorPlatform: Capacitor.getPlatform(),
      
      // Connection information
      onlineStatus: navigator.onLine,
      connectionType: (navigator as any).connection ? 
        (navigator as any).connection.effectiveType : 'unknown',
      
      // Date and time
      timestamp: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      
      // Window information
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    };
  }

  /**
   * Get a formatted string of device information for debugging
   */
  public static getFormattedDeviceInfo(): string {
    const info = this.getDeviceInfo();
    return `
Device Information:
-------------------
Browser: ${this.getBrowserInfo()}
Operating System: ${this.getOSInfo()}
Screen: ${info.screenWidth}×${info.screenHeight} (${info.pixelRatio}x pixel ratio)
Window: ${info.windowWidth}×${info.windowHeight}
Connection: ${info.onlineStatus ? 'Online' : 'Offline'} ${info.connectionType !== 'unknown' ? `(${info.connectionType})` : ''}
Platform: ${info.isNativePlatform ? `Native (${info.capacitorPlatform})` : 'Web'}
Timezone: ${info.timezone}
Language: ${info.language}
Date: ${new Date().toString()}
    `;
  }

  /**
   * Get browser name and version
   */
  public static getBrowserInfo(): string {
    const userAgent = navigator.userAgent;
    let browserName = "Unknown";
    let browserVersion = "";

    // Chrome
    if (userAgent.indexOf("Chrome") > -1) {
      browserName = "Chrome";
      const chromeVersion = userAgent.match(/Chrome\/(\d+\.\d+)/);
      browserVersion = chromeVersion ? chromeVersion[1] : "";
    } 
    // Firefox
    else if (userAgent.indexOf("Firefox") > -1) {
      browserName = "Firefox";
      const firefoxVersion = userAgent.match(/Firefox\/(\d+\.\d+)/);
      browserVersion = firefoxVersion ? firefoxVersion[1] : "";
    } 
    // Safari
    else if (userAgent.indexOf("Safari") > -1 && userAgent.indexOf("Chrome") === -1) {
      browserName = "Safari";
      const safariVersion = userAgent.match(/Version\/(\d+\.\d+)/);
      browserVersion = safariVersion ? safariVersion[1] : "";
    } 
    // Edge
    else if (userAgent.indexOf("Edg") > -1) {
      browserName = "Edge";
      const edgeVersion = userAgent.match(/Edg\/(\d+\.\d+)/);
      browserVersion = edgeVersion ? edgeVersion[1] : "";
    } 
    // Internet Explorer
    else if (userAgent.indexOf("MSIE") > -1 || userAgent.indexOf("Trident/") > -1) {
      browserName = "Internet Explorer";
      const ieVersion = userAgent.match(/(?:MSIE |rv:)(\d+\.\d+)/);
      browserVersion = ieVersion ? ieVersion[1] : "";
    }

    return browserVersion ? `${browserName} ${browserVersion}` : browserName;
  }

  /**
   * Get operating system information
   */
  public static getOSInfo(): string {
    const userAgent = navigator.userAgent;
    let osName = "Unknown";
    let osVersion = "";

    // Windows
    if (userAgent.indexOf("Windows") > -1) {
      osName = "Windows";
      const windowsVersion = userAgent.match(/Windows NT (\d+\.\d+)/);
      if (windowsVersion) {
        const versionMap: {[key: string]: string} = {
          "10.0": "10",
          "6.3": "8.1",
          "6.2": "8",
          "6.1": "7",
          "6.0": "Vista",
          "5.2": "XP 64-bit",
          "5.1": "XP",
        };
        osVersion = versionMap[windowsVersion[1]] || windowsVersion[1];
      }
    } 
    // macOS
    else if (userAgent.indexOf("Macintosh") > -1) {
      osName = "macOS";
      const macVersion = userAgent.match(/Mac OS X (\d+[._]\d+(?:[._]\d+)?)/);
      if (macVersion) {
        osVersion = macVersion[1].replace(/_/g, ".");
      }
    } 
    // iOS
    else if (userAgent.indexOf("iPhone") > -1 || userAgent.indexOf("iPad") > -1 || userAgent.indexOf("iPod") > -1) {
      osName = userAgent.indexOf("iPad") > -1 ? "iPadOS" : "iOS";
      const iosVersion = userAgent.match(/OS (\d+[._]\d+(?:[._]\d+)?)/);
      if (iosVersion) {
        osVersion = iosVersion[1].replace(/_/g, ".");
      }
    } 
    // Android
    else if (userAgent.indexOf("Android") > -1) {
      osName = "Android";
      const androidVersion = userAgent.match(/Android (\d+(?:\.\d+)+)/);
      osVersion = androidVersion ? androidVersion[1] : "";
    } 
    // Linux
    else if (userAgent.indexOf("Linux") > -1) {
      osName = "Linux";
    }

    return osVersion ? `${osName} ${osVersion}` : osName;
  }
}